import React from "react";
import { Link } from "react-router-dom";

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <nav className="py-5 px-6 md:px-12 border-b border-white/[0.08]">
        <div className="max-w-4xl mx-auto flex items-center">
          <Link to="/" className="flex items-center">
            <img src="/favicon white.png" alt="Plutus" className="h-8 w-8 mr-2 animate-logo-spin" />
            <img src="/dark mode text.png" alt="Plutus" className="h-8 w-auto" />
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="bg-white/[0.03] rounded-2xl border border-white/[0.06] p-6 sm:p-8 md:p-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">PRIVACY POLICY NOTICE</h1>
          <p className="text-gray-500 mb-8">LAST UPDATED: January 15, 2026</p>

          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300 mb-6">
              Namsoft Advisors LLC, Namsoft Tech LLC, and our affiliates (collectively, "Namsoft," "we," "us," or "our")
              know that you care about how your personal information is used and disclosed, and we take your privacy
              seriously. Our primary goal is to provide you with exceptional service, and we understand that you may have
              questions or concerns regarding your personal information and how it will be used. To make this privacy
              notice easy to find, we make it available from the home page of the Plutus platform at www.runplutus.com
              (the "Platform"). You may also email us at{" "}
              <a href="mailto:hello@runplutus.com" className="text-gray-300 hover:text-white underline">
                hello@runplutus.com
              </a>{" "}
              with any privacy-related questions you have.
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">
              To Whom Does This Privacy Policy Apply?
            </h2>
            <p className="text-gray-300 mb-4">
              This privacy notice applies to all information we collect through our Services from current and former
              clients of Namsoft Advisors LLC, prospective clients, website and Platform visitors, business contacts and
              partners, and job applicants. For purposes of this privacy policy, "Services" means investment advisory
              services provided by Namsoft Advisors LLC, the Plutus platform operated by Namsoft Tech LLC at
              www.runplutus.com, and any other products, services, content, features, technologies, or functions, and all
              related websites, applications, platforms, and services offered to you by Namsoft.
            </p>
            <p className="text-gray-300 mb-4">
              As the Services evolve we may occasionally update this privacy notice to reflect changes to our business,
              Services, or applicable laws. For clients of Namsoft Advisors LLC, if the revised version includes material
              changes, we will provide you with 30 days prior notice by email and by posting notice of the change on the
              "Privacy Policy" page of our website at www.runplutus.com. Otherwise, the revised Privacy notice will be
              effective as of the published effective date.
            </p>
            <p className="text-gray-300 mb-4">
              Throughout this statement, we use the term "personal information" to describe information that can be
              associated with a specific person and can be used to identify that person. We do not consider personal
              information to include information that has been aggregated and/or anonymized so that it does not identify a
              specific person.
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">What Information Do We Collect?</h2>
            <p className="text-gray-300 mb-4">
              When you visit our website or use the Platform, we collect your IP address, geolocation information, device
              information, and standard web log information, such as your browser type, operating system, and the pages you
              accessed on our website. We also collect usage data and navigation patterns, cookie and tracking data, and
              information in response to contact forms and surveys. If you do not agree to our collection of this
              information, you may not be able to use our Service.
            </p>
            <p className="text-gray-300 mb-4">
              If you are a prospective client, we may collect your name, email address, country of residence, and
              information about your communications with us and your investment interests.
            </p>
            <p className="text-gray-300 mb-4">
              If you open a Namsoft Advisors account, we may collect the following information from you:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
              <li>
                <strong>Account Information</strong>, including your name, email address, phone number, country of
                residence, brokerage account number
              </li>
              <li>
                <strong>Identification Information</strong>, including your age, date of birth, and professional
                information.
              </li>
              <li>
                <strong>Financial Information</strong>, including your Brokerage account data, account balances,
                investment positions, transaction history, investment objectives, risk tolerance, financial goals, and
                portfolio performance. This also includes information pertaining to investment accounts, investment
                transactions, investment positions, investment experience, and investment interests, goals, and
                preferences.
              </li>
              <li>
                <strong>Device and Activity Information</strong>, including information about you from your mobile device
                or computer such as your device type, machine or mobile device identification number, precise geolocation
                information (information that identifies with reasonable specificity your location by using, for instance,
                longitude and latitude coordinates obtained through GPS, Wi-Fi, or cell site triangulation), time zone,
                language setting, browser type, IP address, cookies, and Platform usage patterns. This data may be used for
                fraud and risk prevention purposes.
              </li>
              <li>
                <strong>Communications</strong>, including records of your communications with us through email, phone,
                chat, support tickets, and meeting notes.
              </li>
              <li>
                <strong>Inferences</strong>, including information we derive from your activity and preferences, such as
                risk profiles, investment preferences, and algorithmic recommendations for your portfolio.
              </li>
            </ul>
            <p className="text-gray-300 mb-4">
              We may collect the above information from third parties (such as brokers, analytics companies, etc.).
            </p>
            <p className="text-gray-300 mb-4">
              We are committed to providing a safe and secure service. Therefore, before permitting you to use the
              Services, we may require additional information from you we can use to verify your identity, address or other
              information to comply with law or to manage risk and compliance throughout our relationship. We may also
              obtain information about you from third parties such as identity verification, fraud prevention and similar
              services.
            </p>
            <p className="text-gray-300 mb-4">
              Finally, we may collect additional information from or about you in other ways not specifically described
              here. For example, we may collect information related to your contact with our customer support team, store
              results when you respond to a survey, or when you use another Namsoft service.
            </p>
            <p className="text-gray-300 mb-4">
              The Services are not directed to children under the age of 13, and we do not knowingly collect information
              from children under 13 or from individuals under 16 without parental consent. Investment advisory services
              are intended for adults. If you believe we have mistakenly or unintentionally collected information from a
              child under the age of 13, contact us immediately at{" "}
              <a href="mailto:hello@runplutus.com" className="text-gray-300 hover:text-white underline">
                hello@runplutus.com
              </a>
              . If you are a client and provide information about minor family members for financial planning purposes, we
              use it solely to provide Services you have requested.
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">
              How Do We Use Cookies and Tracking Technologies?
            </h2>
            <p className="text-gray-300 mb-4">
              When you visit our website or use the Platform, we use cookies and other tracking technologies
              (collectively, "Cookies"). We use essential cookies, analytics cookies, and functional cookies on our
              website and Platform. Essential cookies are required for Platform functionality, login, and security.
              Analytics cookies which help us track usage and improve user experience. Functional cookies help us provide
              communications and remember your preferences.
            </p>
            <p className="text-gray-300 mb-4">
              Certain Services may only be available through the use of Cookies, so if you choose to disable or decline
              Cookies, your use of certain Services may be limited or not possible. You may manage cookies through your
              browser settings. You may also opt out of Google Analytics by visiting{" "}
              <a
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white underline"
              >
                https://tools.google.com/dlpage/gaoptout
              </a>
              .
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">
              How Do We Store and Protect Personal Information?
            </h2>
            <p className="text-gray-300 mb-4">
              We store and process your personal information using third party servers located in data centers in the
              United States. This information is protected by physical, electronic and procedural safeguards in compliance
              with applicable US federal and state laws and regulations, including the requirements of SEC Regulation S-P.
            </p>
            <p className="text-gray-300 mb-4">
              We implement technical safeguards including encryption in transit using TLS 1.2 or higher and encryption at
              rest, network firewalls and intrusion detection systems, multi-factor authentication, role-based access
              controls, secure APIs, and regular security updates. We also enforce administrative safeguards including
              limiting access to personal information only for those employees who reasonably require it to fulfill their
              job responsibilities, employee training on data security, background checks, confidentiality agreements, and
              vendor management programs. We maintain a written information security program as required by Regulation S-P
              designed to ensure security and confidentiality of customer records, protect against threats, and prevent
              unauthorized access.
            </p>
            <p className="text-gray-300 mb-4">
              We strive to ensure security on our systems. Despite our efforts, we cannot guarantee that personal
              information may not be accessed, disclosed, altered or destroyed by breach of our administrative, managerial
              and technical safeguards. No method of transmission or storage is 100% secure, and we cannot guarantee
              absolute security. Therefore, we urge you to take adequate precautions to protect your personal information
              as well. You should use strong, unique passwords, never share your Namsoft credentials with anyone, enable
              multi-factor authentication, log out when finished using the Platform, keep your software updated, and report
              any suspicious activity to{" "}
              <a href="mailto:hello@runplutus.com" className="text-gray-300 hover:text-white underline">
                hello@runplutus.com
              </a>
              .
            </p>
            <p className="text-gray-300 mb-4">
              If Namsoft learns of a systems security breach that compromises your information, we may attempt to notify
              you electronically so that you can take appropriate protective steps. By using the Services, you agree that
              Namsoft may communicate with you electronically. Namsoft may post a notice on the website or Platform if a
              security breach occurs. We may also send an email to you at the email address you have provided to us, send
              an in-app notification, or send notice by mail. Depending on where you live, you may have a legal right to
              receive notice of a security breach in writing. To receive free written notice of a security breach (or to
              withdraw your consent from receiving electronic notice of a security breach), please email us at{" "}
              <a href="mailto:hello@runplutus.com" className="text-gray-300 hover:text-white underline">
                hello@runplutus.com
              </a>
              .
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">
              How Long Do We Store Your Personal Information?
            </h2>
            <p className="text-gray-300 mb-4">
              We retain your personal information for as long as needed or permitted in context of the purpose for which
              it was collected and consistent with applicable law.
            </p>
            <p className="text-gray-300 mb-4">The criteria used to determine our retention period is as follows:</p>
            <p className="text-gray-300 mb-4">
              Personal information used for the ongoing relationship between you and Namsoft is stored for as long as you
              remain a client or active user. For clients of Namsoft Advisors LLC, after your advisory relationship ends,
              we retain your information until the end of the fiscal year following the sixth anniversary of the
              termination of your relationship with Namsoft Advisors, based on recordkeeping requirements pursuant to Rule
              204-2 under the Investment Advisers Act of 1940. We may need to keep information longer when required by law,
              such as under Anti-Money Laundering obligations, for litigation, investigations, audit, and compliance
              practices, to protect against legal claims, or when necessary to establish or defend legal claims.
            </p>
            <p className="text-gray-300 mb-4">
              For Platform and analytics data, we retain usage data during your active use of the Platform. Analytics and
              aggregate data may be retained indefinitely in anonymized form. Technical logs are typically retained for
              12-24 months. Information from prospective clients is typically retained for 2-3 years from last contact. Job
              applicant information is retained for 1-2 years after hiring process completion. When information is no
              longer needed and not required by law, we delete or anonymize it.
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">
              How Do We Use the Personal Information We Collect?
            </h2>
            <p className="text-gray-300 mb-4">
              Our primary purpose in collecting personal information is to provide you with a safe, smooth, and efficient
              experience. We may use your personal information to:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
              <li>
                <strong>Provide Investment Advisory Services</strong>, including providing investment advice, executing
                trades through brokers, monitoring and rebalancing portfolios, generating performance reports and
                statements, calculating fees and processing billing, and communicating about your accounts.
              </li>
              <li>
                <strong>Operate Platform Services</strong>, including operating and maintaining the Platform, providing
                dashboards and investment tools, developing new features and improvements, and providing technical
                support.
              </li>
              <li>
                <strong>Maintain Operational and Compliance Functions</strong>, including client onboarding and KYC
                verification, complying with SEC, GLBA, AML, and tax regulations, maintaining required books and records
                under the Investment Advisers Act, conducting risk management and fraud prevention, and responding to
                regulatory inquiries.
              </li>
              <li>
                <strong>Develop and Improve Algorithms</strong>, including developing and improving portfolio
                recommendation algorithms, training machine learning models, analyzing investment outcomes, and
                researching new investment strategies.
              </li>
              <li>
                <strong>Conduct Analytics and Service Improvement</strong>, including analyzing Platform usage, improving
                user experience and functionality, conducting A/B testing, and monitoring performance.
              </li>
              <li>
                <strong>Provide Communications and Marketing</strong>, including sending account updates and confirmations,
                providing service announcements, sharing educational content, and sending marketing communications (from
                which you may opt out).
              </li>
              <li>
                <strong>Fulfill Legal and Security Obligations</strong>, including enforcing our agreements, complying with
                legal obligations, protecting our rights and those of others, and preventing fraud and security incidents.
              </li>
            </ul>
            <p className="text-gray-300 mb-4">
              We also use personal information to prevent potentially fraudulent, prohibited or illegal activities, and
              enforce our User Agreement through the use of our risk and fraud tools which may include use of Account
              Information, Identification Information, Financial Information, Device Information, and Geolocation
              Information. We use this information to create account connections between your Namsoft account and
              third-party accounts or platforms to customize, personalize, measure, and improve our services and the
              content and layout of our website and Platform, to compare information for accuracy and verify it with third
              parties, for our general business purposes, and to perform other duties as required by law.
            </p>
            <p className="text-gray-300 mb-4">
              If you elect to disclose your Geolocation Information, we will use this information to enhance the security
              of the Services and we may use this information to provide you with location-specific options, functionality,
              and offers.
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">
              How Do We Disclose Personal Information to Other Parties?
            </h2>
            <p className="text-gray-300 mb-4">
              Namsoft Advisors LLC and Namsoft Tech LLC share information as necessary to provide Services to you. Both
              entities follow the same privacy and security standards described in this policy. Affiliate sharing is
              permitted under GLBA without requiring opt-out rights.
            </p>
            <p className="text-gray-300 mb-4">We may disclose your personal information with:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
              <li>
                <strong>Brokers</strong>, to facilitate account integration, trade execution, custody services, and
                compliance functions. This sharing requires your authorization when you connect your Brokerage account to
                the Platform.
              </li>
              <li>
                <strong>Cloud and Infrastructure Providers</strong>, for secure data storage and hosting in the United
                States.
              </li>
              <li>
                <strong>Communications Service Providers</strong>, for chat and email.
              </li>
              <li>
                <strong>Analytics Providers</strong>, for usage analysis and user experience improvement.
              </li>
              <li>
                <strong>Identity Verification and Fraud Prevention Services</strong>, for identity verification, fraud
                detection, and AML/KYC compliance.
              </li>
              <li>
                <strong>Regulatory and Law Enforcement Authorities</strong>, including the SEC, state regulators, FINRA,
                FinCEN, IRS, and law enforcement when required by law or if Namsoft is compelled to do so by a subpoena,
                court order or similar legal procedure; when it is necessary to do so to comply with law; or where the
                disclosure of personal information is reasonably necessary to prevent physical harm or financial loss, to
                report suspected illegal activity, or to investigate violations of the Namsoft Terms of Use.
              </li>
              <li>
                <strong>Professional Advisors</strong>, including attorneys, accountants, auditors, and consultants who
                are subject to confidentiality obligations.
              </li>
              <li>
                <strong>Business Transfer Recipients</strong>, companies that Namsoft plans to merge with or be acquired
                by, or in the event of any bankruptcy, a bankruptcy estate. Should such a combination occur, we will
                require that the new combined entity follow this privacy notice with respect to your personal
                information. If your personal information could be used contrary to this statement, you will receive prior
                notice and the opportunity to communicate preferences you may have, if applicable.
              </li>
              <li>
                <strong>Third-Party Account Connections</strong>, other third parties with your consent or at your
                direction to do so, including if you authorize an account connection with a third-party account or
                platform.
              </li>
            </ul>
            <p className="text-gray-300 mb-4">
              All service providers are contractually required to use your information only in connection with the
              services they perform for us and not for their own benefit, maintain appropriate security measures, and
              comply with applicable privacy laws.
            </p>
            <p className="text-gray-300 mb-4">
              For the purposes of this privacy notice, an "account connection" with a third party is a connection you
              authorize or enable between your Namsoft account and a non-Namsoft account, payment instrument, or platform
              that you lawfully control or own. When you authorize such a connection, Namsoft and the third-party will
              exchange your personal information and other information directly. The primary example of an account
              connection is connecting your Namsoft account to your Brokerage account where your financial assets are
              held.
            </p>
            <p className="text-gray-300 mb-4">
              If you connect your Namsoft account to other financial accounts, directly or through a third-party service
              provider, we may have access to your account balance and account and transactional information, such as
              purchases and funds transfers. If you choose to create an account connection, we may receive information
              from the third-party about you and your use of the third-party's service. We will use all such information
              that we receive from a third-party via an account connection in a manner consistent with this privacy notice.
            </p>
            <p className="text-gray-300 mb-4">
              Information that we disclose with a third-party based on an account connection will be used and disclosed in
              accordance with the third-party's privacy practices. Before authorizing an account connection, you should
              review the privacy notice of any third-party that will gain access to your personal information as part of
              the account connection. For example, personal information that Namsoft discloses with a third-party account
              or platform may in turn be disclosed with certain other parties, including the general public, depending on
              the account's or platform's privacy practices.
            </p>
            <p className="text-gray-300 mb-4">
              Namsoft does not sell your personal information or disclose your personal information with third parties for
              their promotional or marketing purposes unrelated to our Services.
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">
              How Can You Access and Change Your Personal Information?
            </h2>
            <p className="text-gray-300 mb-4">
              You can review and update your personal information in your account settings at any time by logging in to
              your account on the Platform. You may also contact us at{" "}
              <a href="mailto:hello@runplutus.com" className="text-gray-300 hover:text-white underline">
                hello@runplutus.com
              </a>{" "}
              to request access to or correction of your personal information.
            </p>
            <p className="text-gray-300 mb-4">
              You may opt out of receiving marketing communications from us by following unsubscribe links in emails,
              emailing{" "}
              <a href="mailto:hello@runplutus.com" className="text-gray-300 hover:text-white underline">
                hello@runplutus.com
              </a>{" "}
              with "Unsubscribe" in the subject line, or managing your preferences in your account settings. You will
              still receive transactional, administrative, and legal communications even if you opt out of marketing.
            </p>
            <p className="text-gray-300 mb-4">
              If you wish to close your account, please contact{" "}
              <a href="mailto:hello@runplutus.com" className="text-gray-300 hover:text-white underline">
                hello@runplutus.com
              </a>
              . We will deactivate your account but must retain certain information as described in the retention section
              above.
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">
              Your State-Specific Privacy Rights
            </h2>
            <p className="text-gray-300 mb-4">
              Residents of California, Colorado, Connecticut, Virginia, Utah, and other states with comprehensive privacy
              laws may have additional rights under state law.
            </p>
            <p className="text-gray-300 mb-4">
              Your rights may include the right to know what personal information we collect about you and to access a
              copy of your personal information; the right to request deletion of your personal information (subject to
              legal retention requirements and exceptions); the right to request correction of inaccurate information
              (available in California, Colorado, Connecticut, and Virginia); the right to receive your information in a
              portable format in some states; the right to opt out of sale of personal information (we do not sell personal
              information), targeted advertising (we do not currently engage in targeted advertising), or sharing for
              cross-context behavioral advertising (our use of analytics tools may constitute "sharing" under some state
              laws, and you may opt out via browser settings or analytics opt-out tools); the right to limit use of
              sensitive information (we use sensitive information only for necessary Services purposes); and the right to
              non-discrimination for exercising your privacy rights.
            </p>
            <p className="text-gray-300 mb-4">
              <strong>How to Exercise Your Rights:</strong> You may exercise these rights by emailing{" "}
              <a href="mailto:hello@runplutus.com" className="text-gray-300 hover:text-white underline">
                hello@runplutus.com
              </a>{" "}
              with "Privacy Rights Request" in the subject line, or by mailing us at Namsoft Advisors LLC, Attention:
              Privacy Officer. Please include your name, email address, state of residence, the specific right(s) you are
              exercising, and sufficient detail for us to respond to your request.
            </p>
            <p className="text-gray-300 mb-4">
              We will respond to your request within 45 days, though we may extend this period for an additional 45 days
              with notice to you. If we deny your request, you may appeal in certain states by contacting us with "Privacy
              Appeal" in the subject line.
            </p>
            <p className="text-gray-300 mb-4">
              <strong>California-Specific Information:</strong> We collect the following categories of personal
              information: identifiers, customer records, protected classifications, commercial information, internet
              activity, geolocation data, audio/visual information, professional information, inferences, and sensitive
              personal information (including precise geolocation and account credentials). While we do not sell personal
              information for money, our use of analytics tools may constitute "sharing" under California law, and you may
              opt out through your browser settings and analytics opt-out tools.
            </p>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-4">How Can You Contact Us?</h2>
            <p className="text-gray-300 mb-4">
              If you have questions or concerns regarding this privacy notice, or any feedback pertaining to your privacy,
              please contact us:
            </p>
            <p className="text-gray-300 mb-2">
              <strong>Email:</strong>{" "}
              <a href="mailto:hello@runplutus.com" className="text-gray-300 hover:text-white underline">
                hello@runplutus.com
              </a>
            </p>
            <p className="text-gray-300 mb-2">
              <strong>Mail:</strong>
            </p>
            <p className="text-gray-300 mb-4">
              Namsoft Advisors LLC
              <br />
              Attention: Privacy Officer.
            </p>
            <p className="text-gray-300">We respond to privacy inquiries as promptly as possible and as required by law.</p>
          </div>
        </div>
      </div>

      <footer className="py-8 px-6 md:px-12 border-t border-white/[0.08]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm text-gray-600">© {new Date().getFullYear()} Plutus. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
